import {
  CANONICAL_STATES,
  TERMINAL_FORECAST_STAGES,
  stageDefaultProbability,
  type Opportunity,
  type Pipeline,
} from "@sales-ops-amplify/shared";

export interface PipelineAnalytics {
  totalJobs: number;
  /** @alias totalJobs */
  totalOpportunities: number;
  inPipeline: number;
  unprocessed: number;
  active: number;
  interviews: number;
  /** @alias interviews */
  demos: number;
  offers: number;
  /** @alias offers */
  won: number;
  rejected: number;
  responseRate: string;
  interviewRate: string;
  /** @alias interviewRate */
  demoRate: string;
  offerRate: string;
  /** @alias offerRate */
  winRate: string;
  applyRate: string;
  /** @alias applyRate */
  proposalRate: string;
  avgScore: string;
  /** sum(amount * probability/100) for open stages */
  weightedPipeline: number;
  stageCounts: Record<string, number>;
  scoreBuckets: [number, number, number, number, number];
}

export function getPipelineForJob(job: Opportunity, pipelines: Pipeline[]): Pipeline | undefined {
  if (job.pipelineId) {
    const byId = pipelines.find(
      (p) => p.id === job.pipelineId || p.pipelineId === job.pipelineId,
    );
    if (byId) return byId;
  }
  return pipelines.find(
    (p) => p.opportunityId === job.id,
  );
}

/** @alias getPipelineForJob */
export const getPipelineForOpportunity = getPipelineForJob;

export function computePipelineAnalytics(jobs: Opportunity[], pipelines: Pipeline[]): PipelineAnalytics {
  const pipedJobs = jobs.filter((j) => j.pipelineId);
  const unprocessed = jobs.length - pipedJobs.length;

  const stageCounts: Record<string, number> = Object.fromEntries(
    CANONICAL_STATES.map((s) => [s, 0]),
  );

  for (const job of pipedJobs) {
    const stage = getPipelineForJob(job, pipelines)?.currentStage ?? "Qualified";
    stageCounts[stage] = (stageCounts[stage] ?? 0) + 1;
  }

  const qualified = stageCounts.Qualified ?? 0;
  const discovery = stageCounts.Discovery ?? 0;
  const proposal = stageCounts.Proposal ?? 0;
  const demo = stageCounts.Demo ?? 0;
  const negotiation = stageCounts.Negotiation ?? 0;
  const won = stageCounts.Won ?? 0;
  const lost = stageCounts.Lost ?? 0;
  const passed = stageCounts.Passed ?? 0;
  const watch = stageCounts.Watch ?? 0;

  const active = discovery + proposal + demo + negotiation;
  const submitted = proposal + demo + negotiation + won + lost;
  const evaluatedTotal = pipedJobs.length - watch - passed;

  const pct = (n: number, d: number) => (d > 0 ? ((n / d) * 100).toFixed(1) : "0.0");

  const scores = pipedJobs
    .map((j) => j.score)
    .filter((s): s is number => s !== undefined && !Number.isNaN(s));
  const avgScore =
    scores.length > 0
      ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
      : "—";

  const scoreBuckets: [number, number, number, number, number] = [0, 0, 0, 0, 0];
  for (const job of pipedJobs) {
    const n = job.score ?? 0;
    if (n >= 4) scoreBuckets[4]++;
    else if (n >= 3) scoreBuckets[3]++;
    else if (n >= 2) scoreBuckets[2]++;
    else if (n >= 1) scoreBuckets[1]++;
    else scoreBuckets[0]++;
  }

  let weightedPipeline = 0;
  for (const job of pipedJobs) {
    const stage =
      getPipelineForJob(job, pipelines)?.currentStage ?? job.stage ?? "Qualified";
    if (TERMINAL_FORECAST_STAGES.has(stage)) continue;
    const amount = typeof job.amount === "number" ? job.amount : 0;
    const probability =
      typeof job.probability === "number"
        ? job.probability
        : stageDefaultProbability(stage);
    weightedPipeline += amount * (probability / 100);
  }

  return {
    totalJobs: jobs.length,
    totalOpportunities: jobs.length,
    inPipeline: pipedJobs.length,
    unprocessed,
    active,
    interviews: demo,
    demos: demo,
    offers: won,
    won,
    rejected: lost,
    responseRate: pct(discovery + demo + negotiation + won, submitted),
    interviewRate: pct(demo, submitted),
    demoRate: pct(demo, submitted),
    offerRate: pct(won, submitted),
    winRate: pct(won, submitted),
    applyRate: pct(proposal + demo + negotiation + won + lost, evaluatedTotal),
    proposalRate: pct(proposal + demo + negotiation + won + lost, evaluatedTotal),
    avgScore,
    weightedPipeline,
    stageCounts: {
      ...stageCounts,
      Qualified: qualified,
      Discovery: discovery,
      Proposal: proposal,
      Demo: demo,
      Negotiation: negotiation,
      Won: won,
      Lost: lost,
      Passed: passed,
      Watch: watch,
    },
    scoreBuckets,
  };
}

export function computeWeeklyActivity(
  jobs: Opportunity[],
  weeks = 8,
): { labels: string[]; values: number[] } {
  const counts = new Map<string, number>();

  for (const job of jobs) {
    const d = job.createdAt;
    if (!d) continue;
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) continue;

    const monday = new Date(date);
    const day = (date.getDay() + 6) % 7;
    monday.setDate(date.getDate() - day);
    const key = monday.toISOString().slice(0, 10);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const sorted = [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
  const slice = sorted.slice(-weeks);
  return {
    labels: slice.map(([k]) => {
      const d = new Date(k);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    }),
    values: slice.map(([, v]) => v),
  };
}

export function topCompanies(
  jobs: Opportunity[],
  limit = 5,
): Array<{ company: string; count: number }> {
  const counts = new Map<string, number>();
  for (const job of jobs.filter((j) => j.pipelineId)) {
    const name = job.company || "Unknown";
    counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([company, count]) => ({ company, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}
